import { HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { compactDecrypt, CompactEncrypt } from 'jose';
import { NGXLogger } from 'ngx-logger';
import { deflateRaw, inflateRaw } from 'pako';

import { Feature } from '../../model/features.enum';
import { StorageKey } from '../../model/storage.enums';
import { Utils } from '../../utils/utils';
import { ApiService } from '../api.service';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';

const asyncInflateRaw = async (input: Uint8Array) => inflateRaw(input);
const asyncDeflateRaw = async (input: Uint8Array) => deflateRaw(input);

export interface EncryptionInfo {
  keyPair: CryptoKeyPair;
  serverKey: CryptoKey;
  siteId: string;
  serverKeySerial: string;
}

export interface EncryptedPayload {
  body: any;
  serverKeySerial: string;
  siteId: string;
}

@Service()
export class EncryptionService {
  #apiService = inject(ApiService);
  #logger = inject(NGXLogger);
  #featureToggle = inject(FeatureToggleService);

  #enabledEncryption = true;
  private encryptionInfo!: Promise<EncryptionInfo>;

  constructor() {
    this.#featureToggle
      .when(Feature.WebBackend)
      .isOff(() => (this.encryptionInfo = this.exchangeKeys()))
      .isOn(() => (this.#enabledEncryption = false))();
  }

  shouldSkipEncryption(req: HttpRequest<unknown>): boolean {
    const isLocalHost = window.location.host.includes('localhost');
    const skipEncryption = ['true', '1'].includes(
      new window.URLSearchParams(window.location.search).get('encrypt') ?? '',
    );
    const decryptedBody = sessionStorage.getItem(StorageKey.DecryptedBody) === 'true';
    const credentialsEnabled = req.withCredentials;
    const hostname = globalThis?.location?.hostname;
    const isLinkApp = hostname?.includes('link') && !hostname?.includes('qlink');

    if (
      (isLocalHost && (skipEncryption || decryptedBody)) ||
      credentialsEnabled ||
      !this.#enabledEncryption ||
      isLinkApp
    ) {
      return true;
    }

    const isExcludedUrl =
      req.url.endsWith('/exchangeKeys') ||
      req.url.endsWith('/haloDotGoUrl') ||
      req.url.endsWith('/appSocketServerUrl') ||
      req.url.endsWith('/register/JWT') ||
      req.url.endsWith('/getMerchantDocuments');

    return !Utils.isExternalUrl(req) || isExcludedUrl;
  }

  async encryptBody(body: any): Promise<EncryptedPayload> {
    const encInfo = await this.encryptionInfo;
    if (!body) {
      return {
        serverKeySerial: encInfo.serverKeySerial,
        siteId: encInfo.siteId,
        body: null,
      };
    }
    const encoder = new TextEncoder();
    const encryptedBody = await new CompactEncrypt(encoder.encode(JSON.stringify(body)))
      .setProtectedHeader({ alg: 'RSA-OAEP', enc: 'A128GCM', zip: 'DEF' })
      .encrypt(encInfo.serverKey, { deflateRaw: asyncDeflateRaw });

    this.#logger.log(encryptedBody);
    return {
      serverKeySerial: encInfo.serverKeySerial,
      siteId: encInfo.siteId,
      body: {
        jwe: encryptedBody,
      },
    };
  }

  async decryptBody(event: HttpEvent<any>, req: HttpRequest<any>): Promise<HttpEvent<any>> {
    if (event instanceof HttpResponse && event.status !== 204) {
      const encInfo = await this.encryptionInfo;
      const decoder = new TextDecoder();
      const { plaintext } = await compactDecrypt(event.body.jwe, encInfo.keyPair.privateKey, {
        inflateRaw: asyncInflateRaw,
      });
      const decryptedBody = JSON.parse(decoder.decode(plaintext));
      this.#logger.log(`HTTP[${req.method}] Response ${req.url}`, decryptedBody);
      return event.clone({ body: decryptedBody });
    }
    return event;
  }

  async decryptError(error: HttpErrorResponse): Promise<never> {
    if (error instanceof HttpErrorResponse && error.error?.jwe != null) {
      const encInfo = await this.encryptionInfo;
      const decoder = new TextDecoder();
      const { plaintext } = await compactDecrypt(error.error.jwe, encInfo.keyPair.privateKey, {
        inflateRaw: asyncInflateRaw,
      });
      const decryptedError = JSON.parse(decoder.decode(plaintext));
      this.#logger.log(decryptedError);

      throw new HttpErrorResponse({
        error: decryptedError,
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined,
      });
    }
    throw error;
  }

  private async exchangeKeys(): Promise<EncryptionInfo> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-1',
      },
      false,
      ['decrypt'],
    );

    const publicKey = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const res = await this.#apiService.exchangeKeys(this.arrayBufferToPem(publicKey));

    const siteId = res.siteIdentifier;
    const serverKeySerial = res.keySerial;
    const serverKey = await crypto.subtle.importKey(
      'spki',
      this.base64ToUint8Array(res.serverKey),
      {
        name: 'RSA-OAEP',
        hash: 'SHA-1',
      },
      true,
      ['encrypt'],
    );

    return {
      keyPair,
      serverKey,
      siteId,
      serverKeySerial,
    };
  }

  private arrayBufferToPem(buffer: ArrayBuffer): string {
    return `-----BEGIN PUBLIC KEY-----\n${btoa(
      String.fromCharCode(...new Uint8Array(buffer)),
    )}\n-----END PUBLIC KEY-----`;
  }

  private base64ToUint8Array(str: string): ArrayBuffer {
    const binString = atob(str);
    const buf = new ArrayBuffer(binString.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = binString.length; i < strLen; i++) {
      bufView[i] = binString.charCodeAt(i);
    }
    return buf;
  }
}
